import axios from 'axios';
import Vue from 'vue';
import { SP_PRODUCTS_ENDPOINT, SP_GRANT_ENDPOINT } from '@/utils/supplierPortal';

// In-flight GET /v1/products, shared by concurrent callers. Module-scoped rather
// than in state: it is not renderable data and must not be reactive.
let productsRequest = null;

const state = {
    // GET /v1/products payload: { purchased[], supplier_portal{}, sourcing{} }
    products: {},
    productsLoaded: false,
    productsLoading: false,
};

const mutations = {
    SET_SP_PRODUCTS(state, products) {
        Vue.set(state, 'products', products || {});
        state.productsLoaded = true;
    },
    SET_SP_PRODUCTS_LOADING(state, loading) {
        state.productsLoading = loading;
    },
};

const actions = {
    /**
     * GET /v1/products (§1) — tenant entitlement plus the role and access-level
     * dropdown options. One call drives checkbox visibility and both dropdowns, so
     * nothing else needs fetching.
     *
     * Cached: the payload is tenant-static and both /adduser and /profile/manageuser
     * need it. Concurrent callers share the in-flight promise.
     *
     * ENVELOPE: the contract is ambiguous for this route — §1 is headed
     * "Response `data`", but the envelope section says /v1/products on R4S_MSA is
     * flat with keys at top level, and Sourcing hits R4S_MSA. Normalise here so we
     * are correct either way; drop the fallback once the backend confirms.
     */
    getProducts({ commit, state }) {
        if (state.productsLoaded) {
            return Promise.resolve(state.products);
        }
        if (productsRequest) {
            return productsRequest;
        }
        commit('SET_SP_PRODUCTS_LOADING', true);
        productsRequest = axios
            .get(`${process.env.VUE_APP_API}${SP_PRODUCTS_ENDPOINT}`)
            .then((res) => {
                const payload = (res && res.data) || {};
                const body = payload.data || payload;
                commit('SET_SP_PRODUCTS', body);
                return body;
            })
            .finally(() => {
                commit('SET_SP_PRODUCTS_LOADING', false);
                // Cleared on failure too, so a transient error can be retried.
                productsRequest = null;
            });
        return productsRequest;
    },

    /**
     * POST /v1/user/supplier_portal/access (§3) — grant portal access to an existing
     * Sourcing user from the edit dialog.
     *
     * Grant-only: it cannot change the portal role and cannot revoke. Offer it only
     * for rows where `supplier_portal.added === false` and `role === 'PRC'`.
     * @param {{user_id: string, role: string, access_level: string[]}} params
     */
    grantSupplierPortalAccess(context, params) {
        return axios.post(`${process.env.VUE_APP_API}${SP_GRANT_ENDPOINT}`, params);
    },
};

const getters = {
    getSupplierPortalProducts(state) {
        return state.products;
    },
    getSupplierPortalRoleOptions(state) {
        return (state.products.supplier_portal && state.products.supplier_portal.roles) || [];
    },
    getSupplierPortalAccessLevelOptions(state) {
        return (state.products.supplier_portal && state.products.supplier_portal.access_levels) || [];
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
